<template>
    <div class="tournament-brackets" v-if="hasBrackets">
        <div class="tabs">
            <template v-for="(bracket, index) in brackets">
                <input
                    :key="`input-${bracket.bracketId}`"
                    type="radio"
                    :id="`bracket-${bracket.bracketId}`"
                    :value="index"
                    v-model="activeTab"
                    @change="handleTabChange(index)" />
                <label
                    :key="`label-${bracket.bracketId}`"
                    class="tab"
                    :for="`bracket-${bracket.bracketId}`">
                    <i class="fa-regular fa-chart-column"></i>
                    <span class="tab-label">{{ bracket.bracketLabel }}</span>
                </label>
            </template>
            <span class="glider" :style="gliderStyle"></span>
        </div>
    </div>
</template>

<script>
import TournamentRepository from '../../../repositories/tournament';

export default {
    name: 'TournamentBrackets',
    props: {
        tournamentId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            brackets: [],
            activeTab: 0,
            loading: false,
            hasBrackets: false,
            initialTournamentId: null,
            bracketsInitialized: false,
            gliderWidth: 140,
            gliderLeft: 0
        };
    },
    computed: {
        organizerColor() {
            if (this.$store.state.tournament?.tournamentSettings?.colors?.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                return color.startsWith('#') ? color : `#${color}`;
            }
            return '#009F63'; // Default green
        },
        gliderStyle() {
            return {
                width: `${this.gliderWidth}px`,
                transform: `translateX(${this.gliderLeft}px)`,
                backgroundColor: this.organizerColor
            };
        }
    },
    watch: {
        tournamentId: {
            immediate: true,
            handler(newTournamentId, oldTournamentId) {
                if (!newTournamentId) return;

                // Check if this is a bracket switch within the same master tournament
                const isBracketSwitch = this.bracketsInitialized &&
                                        newTournamentId !== oldTournamentId &&
                                        this.isBracketTournamentId(newTournamentId);

                if (isBracketSwitch) {
                    // Bracket switch - don't refetch, just update active tab
                    return;
                }

                // For a new tournament (not a bracket switch), reset everything
                if (newTournamentId !== this.initialTournamentId || !this.bracketsInitialized) {
                    // Reset state for new tournament
                    this.brackets = [];
                    this.hasBrackets = false;
                    this.activeTab = 0;
                    this.initialTournamentId = newTournamentId;
                    this.bracketsInitialized = true;
                    this.fetchBrackets();
                }
            }
        }
    },
    methods: {
        isBracketTournamentId(tournamentId) {
            // Check if the tournamentId matches any of the bracket tournament IDs
            return this.brackets.some(bracket => bracket.tournamentId === tournamentId);
        },
        async fetchBrackets() {
            this.loading = true;
            try {
                const response = await TournamentRepository.getTournamentBrackets(this.initialTournamentId);

                if (response.status === 200 && response.data && response.data.length > 0) {
                    this.brackets = response.data;
                    this.hasBrackets = true;

                    // Only emit initial bracket selection on first load
                    if (this.activeTab === 0) {
                        this.$emit('bracket-selected', this.brackets[0]);
                    }

                    // Initialize glider after brackets are rendered
                    this.initGlider();
                } else {
                    this.hasBrackets = false;
                }
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    // No brackets exist - this is normal for non-bracketed tournaments
                    this.hasBrackets = false;
                } else {
                    console.error('Error fetching brackets:', error);
                }
            } finally {
                this.loading = false;
            }
        },
        handleTabChange(tabIndex) {
            if (this.brackets[tabIndex]) {
                this.$emit('bracket-selected', this.brackets[tabIndex]);
                this.updateGlider(tabIndex);
            }
        },
        updateGlider(tabIndex) {
            this.$nextTick(() => {
                const tabs = this.$el.querySelectorAll('.tab');
                if (tabs[tabIndex]) {
                    this.gliderWidth = tabs[tabIndex].offsetWidth;
                    this.gliderLeft = tabs[tabIndex].offsetLeft;
                }
            });
        },
        initGlider() {
            this.$nextTick(() => {
                this.updateGlider(this.activeTab);
            });
        }
    },
    mounted() {
        this.initGlider();
    }
};
</script>

<style scoped lang="scss">
.tournament-brackets {
    width: 100%;
    margin-bottom: 24px;

    .tabs {
        display: flex;
        position: relative;
        background-color: var(--v-lightGray-base);
        border-radius: 10px;
        width: fit-content;

        * {
            z-index: 2;
        }

        input[type="radio"] {
            display: none;

            &:checked {
                & + label {
                    color: white !important;
                }
            }
        }

        .tab {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 140px;
            height: 38px;
            font-size: 14px;
            font-weight: 600;
            color: var(--v-mainBlack-base);
            border-radius: 10px;
            cursor: pointer;
            transition: color 0.15s ease-in;
            font-family: "Avenir", sans-serif;
            padding: 0 16px;

            i {
                padding-right: 8px;
                font-size: 14px;
            }
        }

        .glider {
            position: absolute;
            display: flex;
            height: 38px;
            background-color: var(--v-mainGreen-base);
            border-radius: 10px;
            z-index: 1;
            transition: 0.25s ease-out;
        }
    }
}

.theme--dark .tournament-brackets {
    .tabs {
        background-color: #303740;

        .tab {
            color: var(--v-fullWhite-base);

            &:hover {
                opacity: 0.8;
            }
        }
    }
}
</style>
